import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import { UserObject } from '@/schemas/Users';
import type { IUser } from '@/schemas/Users';
import { createI18n } from 'vue-i18n';
import messages from '../i18n';
import router from '@/router';
import HttpService from '@/services/HttpService';
import Cookies from 'js-cookie';

const i18n = createI18n({
  locale: 'en',
  legacy: false,
  messages,
  globalInjection: true,
});
const { t } = i18n.global;

export const AuthStore = defineStore('auth', {
  state: () => ({
    auth: useStorage('auth', {
      user: UserObject,
      userType: '',
      isProcessing: false,
      accessToken: '',
      refreshToken: '',
    }),
  }),
  actions: {
    setUser(user: IUser) {
      const wasLoggedOut = !this.auth.user?.id;
      this.auth.user = user;
      if (user?.id) {
        window.dataLayer = window.dataLayer || [];
        if (wasLoggedOut) {
          window.dataLayer.push({
            event: 'user_authenticated',
            isEducator: !!user.orgContactId,
            isAdmin: user.orgContactRole === 'admin',
            isStaff: user.isStaff,
            isSuperuser: user.isSuperuser,
          });
        } else {
          window.dataLayer.push({
            isEducator: !!user.orgContactId,
            isAdmin: user.orgContactRole === 'admin',
            isStaff: user.isStaff,
            isSuperuser: user.isSuperuser,
          });
        }
      }
    },
    async logout() {
      this.setIsProcessing(true);

      try {
        // Try to logout user from backend
        // Use a timeout to prevent hanging if API is down
        await Promise.race([
          HttpService.get('/logout/'),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Logout timeout')), 3000),
          ),
        ]);
      } catch (error) {
        // Silently handle logout API errors - we still want to clear local state
        console.warn('Backend logout failed, but continuing with local logout:', error);
      }

      // Always clear local state regardless of API success/failure
      this.clearLocalAuth();
      this.setIsProcessing(false);
    },

    setTokens(accessToken: string, refreshToken: string) {
      this.auth.accessToken = accessToken;
      this.auth.refreshToken = refreshToken;
    },
    clearTokens() {
      this.auth.accessToken = '';
      this.auth.refreshToken = '';
    },

    // Separate method for clearing local authentication state
    clearLocalAuth() {
      // Clear Django authentication cookies
      Cookies.remove('sessionid'); // Django session cookie
      Cookies.remove('csrftoken'); // Django CSRF token cookie

      // Clear JWT tokens
      this.clearTokens();

      // Clear user object
      this.setUser({} as IUser);

      // Redirect to login page
      router.push({
        name: 'login-view',
        query: { reason: 'session-expired' },
      });
    },
    setIsProcessing(processing: boolean) {
      this.auth.isProcessing = processing;
    },
  },
  getters: {
    getUser(): IUser {
      return this.auth.user;
    },
    getIsLoggedIn(): boolean {
      return !!this.auth.user.id;
    },
    getProcessing(): boolean {
      return this.auth.isProcessing;
    },
    getAccessToken(): string {
      return this.auth.accessToken ?? '';
    },
  },
});
